import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import CardTitle from "@saleor/components/CardTitle";
import PriceField from "@saleor/components/PriceField";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import { getFormErrors, getProductErrorMessage } from "@saleor/utils/errors";
import createNonNegativeValueChangeHandler from "@saleor/utils/handlers/nonNegativeValueChangeHandler";
import React from "react";
import { useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    root: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr"
    }
  }),
  { name: "ProductPricing" }
);

interface ProductPricingProps {
  currency?: string;
  data: {
    basePrice: number;
    salePrice: string;
  };
  disabled: boolean;
  isStoreAdmin?: boolean;
  isSellerMaster?: boolean;
  errors: ProductErrorFragment[];
  validate: {
    basePrice: null;
  };
  onChange: (event: React.ChangeEvent<any>) => void;
}

const ProductPricing: React.FC<ProductPricingProps> = props => {
  const {
    currency,
    data,
    disabled,
    isStoreAdmin,
    isSellerMaster,
    validate,
    errors,
    onChange
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();

  const formErrors = getFormErrors(["basePrice"], errors);

  const handlePriceChange = createNonNegativeValueChangeHandler(onChange);

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Pricing Plans",
          description: "product pricing"
        })}
      />
      <CardContent>
        <div className={classes.root}>
          <PriceField
            idPriceField="retailPrice"
            disabled={disabled}
            label={intl.formatMessage({
              defaultMessage: "Retail Price",
              description: "product retail price"
            })}
            error={!!formErrors.basePrice || !!validate?.basePrice}
            hint={
              getProductErrorMessage(formErrors.basePrice, intl) ||
              validate?.basePrice
            }
            name="basePrice"
            value={data.basePrice}
            currencySymbol={currency}
            onChange={handlePriceChange}
            InputProps={{
              inputProps: {
                min: "0"
              },
              readOnly: isStoreAdmin
            }}
          />
          {!isSellerMaster && (
            <PriceField
              idPriceField="sellingPrice"
              disabled={disabled}
              label={intl.formatMessage({
                defaultMessage: "Selling Price",
                description: "product selling price"
              })}
              name="salePrice"
              value={data.salePrice}
              currencySymbol={currency}
              onChange={handlePriceChange}
              InputProps={{
                inputProps: {
                  min: "0"
                },
                readOnly: true
              }}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};
ProductPricing.displayName = "ProductPricing";
export default ProductPricing;
